import { useForm } from 'react-hook-form';
import { createRef, useRef, useState } from 'react';
import Image from 'next/image';
import Checkbox from '@/components/common/Checkbox';
import Button from '@/components/common/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { isSubmittedFieldValid } from '@/utilities/isSubmittedFieldValid';
import clsx from 'clsx';

interface BrandApplication {
  brandName: string;
  website: string;
  contactName: string;
  contactEmail: string;
  brandCategory: string[];
  brandCategoryOther?: string;
  currentRetailers: string;
  eCommercePlatform: string[];
  eCommercePlatformOther?: string;
  message: string;
}

const BrandApplicationForm = () => {
  const [isValid, setValid] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const {
    register,
    reset,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm<BrandApplication>();

  const { currentRetailers } = getValues()

  const onSubmit = async (data: BrandApplication) => {
    setError(false);
    setIsSubmitted(false);
    setIsSubmitting(true);

    if (!data.brandCategory) {
      data.brandCategory = [];
    }

    if (!data.eCommercePlatform) {
      data.eCommercePlatform = [];
    }

    if (
      (data.brandCategory && !data.brandCategory?.includes('other')) ||
      !data.brandCategoryOther
    ) {
      data.brandCategoryOther = undefined;
    }

    if (
      (data.eCommercePlatform && !data.eCommercePlatform?.includes('other')) ||
      !data.eCommercePlatformOther
    ) {
      data.eCommercePlatformOther = undefined;
    }
    console.log(data);
    fetch('/api/brand-application', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then((res) => {
      if (res.status === 200) {
        setIsSubmitted(true);
        setIsSubmitting(false);
        reset();
      } else {
        setIsSubmitting(false);
        setError(true);
      }
    });
  };

  const validateEmail = (email: string): void => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const result = regex.test(email);
    setValid(result);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-9 lg:mt-12 w-full mx-auto container lg:w-2/4"
    >
      <label>
        <p>Brand Name*</p>
        <div className="relative">
          <input
            className={clsx(`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`, {
              'border-[#E47869] focus:border-[#E47869]': errors.brandName,
              'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                isSubmittedFieldValid('brandName', errors) &&
                !errors.brandName
            })}
            type="text"
            {...register('brandName', {
              required: 'Brand Name is required'
            })}
          />
          <span
            className={clsx('absolute right-3 top-6 scale-125', {
              'opacity-0':
                !isSubmittedFieldValid('brandName', errors) ||
                errors.brandName
            })}
          >
            <Image
              src="/icons/green-check-mark.svg"
              alt="Correct"
              width={12}
              height={9}
            />
          </span>
        </div>
        {
          errors.brandName && (
            <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
              <span className="mr-4">
                <ErrorMessage message={errors.brandName.message as string} />
              </span>
              <Image
                src="/exclamation.svg"
                alt="Correct"
                width={15}
                height={15}
              />
            </div>
          )
        }
      </label > {' '}
      < label >
        <p className="mt-7">Website</p>
        <div className="relative">
          <input
            className={clsx(`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`, {
              'border-[#E47869] focus:border-[#E47869]': errors.website,
              'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                isSubmittedFieldValid('website', errors) &&
                !errors.website
            })}
            type="text"
            {...register('website', {
              required: 'Website is required'
            })}
          />
          <span
            className={clsx('absolute right-3 top-6 scale-125', {
              'opacity-0':
                !isSubmittedFieldValid('website', errors) ||
                errors.website
            })}
          >
            <Image
              src="/icons/green-check-mark.svg"
              alt="Correct"
              width={12}
              height={9}
            />
          </span>
        </div>
        {
          errors.website && (
            <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
              <span className="mr-4">
                <ErrorMessage message={errors.website.message as string} />
              </span>
              <Image
                src="/exclamation.svg"
                alt="Correct"
                width={15}
                height={15}
              />
            </div>
          )
        }
      </label > {' '}
      < label >
        <p className="mt-7">Contact name*</p>
        <div className="relative">
          <input
            className={clsx(`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`, {
              'border-[#E47869] focus:border-[#E47869]': errors.contactName,
              'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                isSubmittedFieldValid('contactName', errors) &&
                !errors.contactName
            })}
            type="text"
            {...register('contactName', {
              required: 'Contact name is required'
            })}
          />
          <span
            className={clsx('absolute right-3 top-6 scale-125', {
              'opacity-0':
                !isSubmittedFieldValid('contactName', errors) ||
                errors.contactName
            })}
          >
            <Image
              src="/icons/green-check-mark.svg"
              alt="Correct"
              width={12}
              height={9}
            />
          </span>
        </div>
      </label > {' '}
      < label >
        <p className="mt-7">Contact email address*</p>
        <div className="relative">
          <input
            className={clsx(`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`, {
              'border-[#E47869] focus:border-[#E47869]': errors.contactEmail,
              'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                isSubmittedFieldValid('contactEmail', errors) &&
                !errors.contactEmail
            })}
            type="text"
            {...register('contactEmail', {
              required: 'Contact email is required',
              onChange: (e) => validateEmail(e.target.value),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,5}$/i,
                message: 'This field must contain a valid email.'
              }
            })}
          />
          <span
            className={clsx('absolute right-3 top-6 scale-125', {
              'opacity-0':
                !isSubmittedFieldValid('contactEmail', errors) ||
                errors.contactEmail
            })}
          >
            <Image
              src="/icons/green-check-mark.svg"
              alt="Correct"
              width={12}
              height={9}
            />
          </span>
        </div>

        {
          errors.contactEmail && (
            <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
              <span className="mr-4">
                <ErrorMessage message={errors.contactEmail.message as string} />
              </span>
              <Image
                src="/exclamation.svg"
                alt="Correct"
                width={15}
                height={15}
              />
            </div>
          )
        }

        {
          isValid && (
            <div className="absolute top-4 right-4">
              <Image
                alt="Success"
                src="/icons/check.svg"
                width={16}
                height={16}
              ></Image>
            </div>
          )
        }
      </label >
      <label>
        <p className="mt-7 mb-5">Brand Category:</p>
        <div className="space-y-3">
          <label>
            <Checkbox
              {...register('brandCategory')}
              value="skincare"
              labelClassName="ml-2"
              label="Skincare"
            />{' '}
          </label>
          <label className="leading-[140%] flex flex-row items-center">
            <Checkbox
              {...register('brandCategory')}
              value="haircare"
              label="Haircare"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="leading-[140%] flex flex-row items-center">
            <Checkbox
              {...register('brandCategory')}
              value="makeup"
              label="Makeup"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="leading-[140%] flex flex-row items-center">
            <Checkbox
              {...register('brandCategory')}
              value="sexual"
              label="Sexual Wellness"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="flex flex-row items-center">
            <Checkbox {...register('brandCategory')} value="other" />
            <input
              {...register('brandCategoryOther')}
              className="ml-2 border pl-3 py-3 w-full focus:outline-none focus:border-onyx h-10"
              type="text"
              placeholder="Other"
            />
          </label>
        </div>
      </label>
      <label>
        <p className="mt-7">Current retailers</p>
        <div className='relative'>
          <input
            className={clsx(`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`, {
              'border-[#E47869] focus:border-[#E47869]': errors.currentRetailers,
              'border-b-[#90D4BC] border-b-4 focus:border-[#90D4BC]':
                currentRetailers && currentRetailers !== ''
            })}
            type="text"
            {...register('currentRetailers')}
          />
          <span
            className={clsx('absolute right-3 top-6 scale-125', {
              'opacity-0':
                !currentRetailers && currentRetailers === ''
            })}
          >
            <Image
              src="/icons/green-check-mark.svg"
              alt="Correct"
              width={12}
              height={9}
            />
          </span>
        </div>
      </label>
      <label className="relative">
        <p className="mt-7 mb-5">Platform e-commerce:</p>
        <div className="space-y-3">
          <label>
            <Checkbox
              {...register('eCommercePlatform')}
              value="shopify"
              label="shopify"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="flex flex-row items-center">
            <Checkbox
              {...register('eCommercePlatform')}
              value="wordpress"
              label="wordpress"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="flex flex-row items-center">
            <Checkbox
              {...register('eCommercePlatform')}
              value="wix"
              label="wix"
              labelClassName="ml-2"
            />{' '}
          </label>
          <label className="flex flex-row items-center">
            <Checkbox
              {...register('eCommercePlatform')}
              value="squarespace"
              label="squarespace"
              labelClassName="ml-2"
            />{' '}
          </label>
          <div className="flex flex-row items-center">
            <Checkbox {...register('eCommercePlatform')} value="other" />
            <input
              {...register('eCommercePlatformOther')}
              className="ml-2 border pl-3 py-3 w-full focus:outline-none focus:border-onyx h-10"
              type="text"
              placeholder="Other"
            />
          </div>
        </div>
      </label>
      <label>
        <p className="mt-7">Message</p>
        <textarea
          className={`border pl-3 py-3 w-full focus:outline-none focus:border-onyx mt-2.5`}
          {...register('message')}
        />
      </label>
      {
        isSubmitted && (
          <div className="mt-5">
            <p className="text-center text-onyx font-bold">
              Thank you for your application. We will get back to you shortly.
            </p>
          </div>
        )
      }
      {
        error && (
          <div className="text-sm flex items-center justify-between my-4 text-[#E47869]">
            <span className="mr-4">
              <ErrorMessage
                message={'Something went wrong. Please try again later.'}
              />
            </span>
            <Image src="/exclamation.svg" alt="Correct" width={15} height={15} />
          </div>
        )
      }
      <div className="text-center">
        <button
          className="bg-onyx border py-4 mx-auto w-full md:w-4/6 text-white mt-5 uppercase"
          type="submit"
        >
          SUBMIT
        </button>
      </div>
    </form >
  );
};

export default BrandApplicationForm;
